import { useCallback, useEffect, useState } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { locationAtom } from '@core/Atoms/Search/LocationAtom';
import {
  POSTCODE_KEY,
  FILTERS_PROPS,
  REGION_KEY,
  DEFAULT_DISTANCE,
} from '@core/Constants/Filters/LocationFilter.constants';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { Filter, FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { commonApiURL } from '@core/Environment/Variables';
import { fetchJson } from '@core/Utils/Fetch/FetchJson';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { AutoCompleteOptionProps } from '@gds/Autocomplete/Components/AutocompleteOption.types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  LocationSearch,
  LocationType,
} from 'Components/Plp/Filters/Components/LocationFilter/LocationFilter.entity';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';
import { createArrayRange } from 'Utils/Array/CreateArrayRange';
import { safeJsonParse } from 'Utils/Json/SafeJsonParse';
import { getClosestIndexInRange } from 'Utils/SearchFilters/GetClosestIndexInRange';
import getRegionsOptions from 'Utils/SearchFilters/GetRegionsOptions';

import { trackFilterEvent } from '../../Filters.tracking';

import { MAX_DISTANCE, DISTANCE_STEP, MAX_DISTANCE_KEY } from './LocationFilter.constants';

const useLocationFilter = (errorMessage: string, locale: Locale, applyButton?: boolean) => {
  const filtersList = useAtomValue(filtersListAtom);
  const postcode = getFilterFromArray(POSTCODE_KEY, filtersList)?.value as string | undefined;
  const DISTANCE_RANGE = createArrayRange(5, MAX_DISTANCE[locale], DISTANCE_STEP);
  const { totalCount } = useAtomValue(aggregationsAtom);

  const distance =
    getFilterFromArray(MAX_DISTANCE_KEY, filtersList)?.value ||
    DISTANCE_RANGE[DISTANCE_RANGE.length - 1];

  const regionFilters = FILTERS_PROPS.filter(
    filter =>
      filter.filterType === REGION_KEY &&
      (!filter.restrictLocales || filter.restrictLocales?.includes(locale)),
  ).map(filter => filter.key);

  const isRegionSearch = filtersList?.some(filter => regionFilters.includes(filter.id));
  const [error, setError] = useState('');
  const { updateFilters } = useUpdateFilters();
  const [pendingFilters, setPendingFilters] = useState<Filter[]>([]);
  const [localSliderValue, setLocalSliderValue] = useState(
    getClosestIndexInRange(Number(distance), DISTANCE_RANGE),
  );
  const [localSliderDisplayValue, setLocalSliderDisplayValue] = useState(
    DISTANCE_RANGE[localSliderValue] || 1,
  );
  const [suggestions, setSuggestions] = useState<AutoCompleteOptionProps[]>([]);
  const [regionSugestions, setRegionSugestions] = useState<AutoCompleteOptionProps[]>([]);
  const [storedPostcodeData, setPostcode] = useAtom(postcodeAtom);
  const setLocationState = useSetAtom(locationAtom);

  const [searchValue, setSearchValue] = useState(postcode || '');
  const [locationType, setLocationType] = useState<LocationType>(POSTCODE_KEY);

  const fetchRegions = async () => {
    try {
      const response = await getRegionsOptions();
      error && setError('');
      response && setRegionSugestions(response);
    } catch (error) {
      setError(errorMessage);
    }
  };
  const mainFilterKey = FILTERS_PROPS.find(
    filter =>
      filter.filterType === locationType &&
      filter.isMainFilterKey &&
      (!filter.restrictLocales || filter.restrictLocales?.includes(locale)),
  );

  useEffect(() => {
    if (locationType === REGION_KEY) fetchRegions();
    setSearchValue(
      (getFilterFromArray(mainFilterKey?.key ?? POSTCODE_KEY, filtersList)?.value as string) || '',
    );
  }, [locationType, filtersList]);

  useEffect(() => {
    setLocationType(isRegionSearch ? REGION_KEY : POSTCODE_KEY);
  }, [filtersList]);

  const handleChange = (selectedOption: AutoCompleteOptionProps) => {
    const value =
      typeof selectedOption.value === 'string' ? safeJsonParse(selectedOption.value) : {};
    const allValues = { ...value, [`${MAX_DISTANCE_KEY}`]: DEFAULT_DISTANCE[locale] };

    if (value.postcode && selectedOption?.id) {
      setPostcode({
        ...storedPostcodeData,
        [locale]: selectedOption?.id?.toString(),
      });
      setLocationState({
        error: false,
        isValid: true,
        location: { lat: allValues.lat, lon: allValues.lon },
      });
    }

    setSearchValue(selectedOption.label);

    const filterChanges: Filter[] = FILTERS_PROPS.map(filterProp => {
      const { key, filterType } = filterProp;

      if (locationType === filterType && allValues[key]) {
        return { id: key, value: allValues[key]?.toString() };
      }

      return { id: key, value: undefined };
    });

    const additionalFilters: Filter[] = [
      { id: 'region', value: undefined },
      { id: 'area', value: undefined },
      { id: 'dealer-id', value: undefined },
      { id: 'dealership-id', value: undefined },
    ];
    const updatedFilterChanges: Filter[] = [...additionalFilters, ...filterChanges];

    if (applyButton) {
      setPendingFilters(updatedFilterChanges);
    } else {
      trackFilterEvent(locationType, selectedOption.label, updatedFilterChanges);
      updateFilters(updatedFilterChanges);
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = e.target.value;

    setSearchValue(newSearchValue);
    const headers = { 'x-heycar-tenant': locale };

    if (locationType === POSTCODE_KEY) {
      try {
        const response = await fetchJson<LocationSearch>(
          `${commonApiURL}/location/search?booked=false&q=${newSearchValue}&seed=empirical_ranking_no_random&size=5`,
          { headers },
        );

        const zipCodes = response?.content;
        const newSuggestions = zipCodes.map(item => {
          const place = `${item.postcode} ${item.placeName}`;
          const value = { ...item.location, postcode: place };
          return {
            id: item.postcode,
            value: JSON.stringify(value),
            label: place,
          };
        });
        error && setError('');

        setSuggestions(newSuggestions);
      } catch (error) {
        setError(errorMessage);
      }
    }
  };

  const resetAllFilter = () => {
    const filtersToReset: Filter[] = FILTERS_PROPS.map(filter => ({
      id: filter.key,
      value: undefined,
    }));
    if (applyButton) {
      setPendingFilters(filtersToReset);
    } else {
      updateFilters(filtersToReset);
    }
  };

  const canReset = () => {
    return (
      filtersList.filter(fltr => {
        const idsToCheck: FilterKey[] = FILTERS_PROPS.filter(
          filter => !filter.restrictLocales || filter.restrictLocales?.includes(locale),
        ).map(filter => filter.key);
        return idsToCheck.includes(fltr.id);
      }).length > 0
    );
  };

  const onSliderAfterChange = (values: [number, number]) => {
    if (values[0] === DISTANCE_RANGE.length - 1) {
      setLocalSliderValue(values[0]);
      setLocalSliderDisplayValue(DISTANCE_RANGE[values[0]] || 1);
      if (applyButton) {
        setPendingFilters([{ id: MAX_DISTANCE_KEY, value: undefined }]);
      } else {
        trackFilterEvent(MAX_DISTANCE_KEY, 'All', filtersList);
        updateFilters([{ id: MAX_DISTANCE_KEY, value: undefined }]);
      }
    } else {
      const filterValue = `${DISTANCE_RANGE[values[0]] || 1}`;
      setLocalSliderValue(values[0]);
      setLocalSliderDisplayValue(DISTANCE_RANGE[values[0]] || 1);
      if (applyButton) {
        setPendingFilters([{ id: MAX_DISTANCE_KEY, value: filterValue }]);
      } else {
        trackFilterEvent(MAX_DISTANCE_KEY, filterValue, filtersList);
        updateFilters([{ id: MAX_DISTANCE_KEY, value: filterValue }]);
      }
    }
  };

  const handleApply = useCallback(() => {
    trackFilterEvent(locationType, [...Object.keys(pendingFilters)], pendingFilters);
    updateFilters([...pendingFilters]);
    setPendingFilters([]);
  }, [applyButton, pendingFilters, updateFilters]);

  const selectedRangeIndexes: [number] = [getClosestIndexInRange(Number(distance), DISTANCE_RANGE)];

  return {
    handleChange,
    resetAllFilter,
    canReset,
    handleInputChange,
    suggestions: locationType === POSTCODE_KEY ? suggestions : regionSugestions,
    setSearchValue,
    searchValue,
    error,
    locationType,
    setLocationType,
    totalCount,
    sliderProps: {
      onAfterChange: onSliderAfterChange,
      stepCount: DISTANCE_RANGE.length,
      isSingleKnob: true,
      disabled: !postcode,
      selectedRangeIndexes: applyButton ? [localSliderValue] : selectedRangeIndexes,
    },
    handleApply,
    localSliderDisplayValue,
  };
};

export default useLocationFilter;
